import React, { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * MOD-001 — graph-level globals with «spør ved bruk» (ask_on_use).
 * Unlocked node fields already act as ask-on-use; globals need this flag.
 */
export default function ModuleGlobalsPanel({ globals = [], onChange, locked = false }) {
  const { t } = useTranslation();
  const [draftKey, setDraftKey] = useState("");
  const [draftLabel, setDraftLabel] = useState("");

  const list = Array.isArray(globals) ? globals : [];

  const updateAt = (index, patch) => {
    onChange(list.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const removeAt = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };

  const addGlobal = () => {
    const key = draftKey.trim().replace(/\s+/g, "_");
    if (!key || locked) return;
    if (list.some((g) => g.key === key)) return;
    onChange([
      ...list,
      {
        key,
        label: draftLabel.trim() || key,
        value: "",
        unit: "",
        ask_on_use: true,
      },
    ]);
    setDraftKey("");
    setDraftLabel("");
  };

  return (
    <div className="offer-module-builder__globals mt-4">
      <div className="offer-module-builder__section-kicker">
        {t("offer.calculation.module.globals.title")}
      </div>
      <p className="offer-module-builder__hint">
        {t("offer.calculation.module.globals.hint")}
      </p>
      <ul className="mt-2 space-y-2">
        {list.map((global, index) => (
          <li
            key={global.key || index}
            className="rounded border border-slate-200 bg-white p-2 text-sm"
          >
            <div className="flex items-center justify-between gap-2">
              <strong className="truncate">{global.label || global.key}</strong>
              <button
                type="button"
                className="offer-module-builder__button"
                disabled={locked}
                onClick={() => removeAt(index)}
              >
                {t("offer.calculation.module.globals.remove")}
              </button>
            </div>
            <div className="mt-1 grid gap-1 text-xs text-slate-600">
              <span className="font-mono">{global.key}</span>
              <label className="flex items-center gap-2">
                <span>{t("offer.calculation.module.globals.value")}</span>
                <input
                  className="min-w-0 flex-1 rounded border border-slate-300 px-1 py-0.5"
                  disabled={locked}
                  value={global.value ?? ""}
                  onChange={(e) => updateAt(index, { value: e.target.value })}
                />
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  disabled={locked}
                  checked={global.ask_on_use !== false}
                  onChange={(e) => updateAt(index, { ask_on_use: e.target.checked })}
                />
                <span>{t("offer.calculation.module.globals.ask_on_use")}</span>
              </label>
            </div>
          </li>
        ))}
      </ul>
      {!locked ? (
        <div className="mt-2 grid gap-1">
          <input
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            placeholder={t("offer.calculation.module.globals.key_placeholder")}
            value={draftKey}
            onChange={(e) => setDraftKey(e.target.value)}
          />
          <input
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            placeholder={t("offer.calculation.module.globals.label_placeholder")}
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
          />
          <button
            type="button"
            className="offer-module-builder__button is-primary"
            onClick={addGlobal}
          >
            {t("offer.calculation.module.globals.add")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
